import React, { useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../DeviceConfigForm.module.css";
import {
  DeviceConfig,
  NfElectrode,
  NfWave,
  NfWaveBand,
  NfWaveMode,
} from "../../../../services/types/device.types";
import {
  DEFAULT_DURATION_MINUTES,
  NF_WAVE_BANDS,
  NF_WAVE_MODES,
  getActiveNfWaves,
  isNfSiteSelected,
  toggleNfSite,
  updateNfWave,
} from "../../../../utils/deviceConfiguration";
import ElectrodeGrid from "./ElectrodeGrid";

interface NeurofeedbackPanelProps {
  config: DeviceConfig;
  onChange: (patch: Partial<DeviceConfig>) => void;
}

function NeurofeedbackPanel({ config, onChange }: NeurofeedbackPanelProps) {
  const electrodes = config.nfElectrodes ?? [];
  const [openSite, setOpenSite] = useState<string | null>(null);
  const openElectrode = electrodes.find((e) => e.site === openSite) ?? null;

  const handleSiteClick = (site: string) => {
    if (isNfSiteSelected(config, site)) {
      // A selected site opens its wave protocol; the ✕ inside the panel removes it.
      setOpenSite(openSite === site ? null : site);
      return;
    }
    onChange({ nfElectrodes: toggleNfSite(config, site) });
    setOpenSite(site);
  };

  const handleRemoveSite = (site: string) => {
    onChange({ nfElectrodes: toggleNfSite(config, site) });
    setOpenSite(null);
  };

  const handleWaveChange = (site: string, band: NfWaveBand, patch: Partial<NfWave>) => {
    onChange({ nfElectrodes: updateNfWave(config, site, band, patch) });
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label}>
          {t("DEVICE_ELECTRODES_ACTIVE")} <span className={styles.req}>*</span>
        </label>
        <ElectrodeGrid
          stateOf={(site) => (isNfSiteSelected(config, site) ? "active" : "unused")}
          onSiteClick={handleSiteClick}
          openSite={openSite}
        />
        <div className={styles.legend}>
          <span>
            <i className={`${styles.legendDot} ${styles.legend_active}`} />
            {t("DEVICE_ACTIVE")}
          </span>
          <span>
            <i className={`${styles.legendDot} ${styles.legend_unused}`} />
            {t("DEVICE_UNUSED")}
          </span>
        </div>
        <p className={styles.hintText}>{t("DEVICE_NF_ELECTRODE_HINT")}</p>
      </div>

      {openElectrode && (
        <div className={styles.fanPanel}>
          <div className={styles.fanHeader}>
            <span className={styles.fanTitle}>
              {openElectrode.site} — {t("DEVICE_WAVES")}
            </span>
            <button
              type="button"
              className={styles.fanRemove}
              onClick={() => handleRemoveSite(openElectrode.site)}
            >
              {t("REMOVE")}
            </button>
          </div>
          <div className={styles.waveList}>
            {openElectrode.waves.map((wave) => {
              const bandMeta = NF_WAVE_BANDS.find((b) => b.key === wave.band);
              return (
                <div key={wave.band} className={styles.waveRow}>
                  <span className={styles.waveName}>
                    {bandMeta ? t(bandMeta.labelKey) : wave.band}
                  </span>

                  {wave.band === "custom" && (
                    <input
                      type="text"
                      className={styles.rangeInput}
                      placeholder={t("DEVICE_WAVE_CUSTOM_PLACEHOLDER")}
                      value={wave.customRange ?? ""}
                      onChange={(e) =>
                        handleWaveChange(openElectrode.site, "custom", {
                          customRange: e.target.value,
                        })
                      }
                    />
                  )}

                  <div className={styles.modeToggle}>
                    {NF_WAVE_MODES.map((mode) => (
                      <button
                        key={mode.value}
                        type="button"
                        className={`${styles.modeBtn} ${
                          (wave.mode ?? "ignore") === mode.value
                            ? styles[`modeBtn_${mode.value}`]
                            : ""
                        }`}
                        onClick={() =>
                          handleWaveChange(openElectrode.site, wave.band, {
                            mode: mode.value as NfWaveMode,
                          })
                        }
                      >
                        {t(mode.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {electrodes.length > 0 && (
        <div className={styles.field}>
          <label className={styles.label}>{t("DEVICE_NF_PROTOCOL_SUMMARY")}</label>
          <ul className={styles.summaryList}>
            {electrodes.map((electrode) => (
              <li key={electrode.site}>
                <button
                  type="button"
                  className={styles.summarySite}
                  onClick={() => setOpenSite(electrode.site)}
                >
                  {electrode.site}
                </button>
                <span className={styles.summaryValue}>{describeWaves(electrode)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label}>
            {t("DEVICE_DURATION_MINUTES")} <span className={styles.req}>*</span>
          </label>
          <input
            type="number"
            min={1}
            className={styles.input}
            value={config.durationMinutes ?? DEFAULT_DURATION_MINUTES}
            onChange={(e) =>
              onChange({ durationMinutes: e.target.value ? Number(e.target.value) : undefined })
            }
          />
        </div>
        {/* The cadence moved out of here: it is now the shared frequency list above, the same one
            the treatment plan uses, and it applies to every modality rather than only this one. */}
      </div>
    </>
  );
}

function describeWaves(electrode: NfElectrode): string {
  const active = getActiveNfWaves(electrode);
  if (active.length === 0) return "—";

  return active
    .map((wave) => {
      const bandMeta = NF_WAVE_BANDS.find((b) => b.key === wave.band);
      const band =
        wave.band === "custom"
          ? wave.customRange || t("DEVICE_WAVE_CUSTOM")
          : bandMeta
          ? t(bandMeta.labelKey)
          : wave.band;
      const mode = NF_WAVE_MODES.find((m) => m.value === wave.mode);
      return `${band} · ${mode ? t(mode.labelKey) : wave.mode}`;
    })
    .join(", ");
}

export default NeurofeedbackPanel;
